import { getQueryKey as getSessionQueryKey } from "@core/hooks/useSession";
import { APIErrorType } from "@core/services/nocd-api";
import { SSRError } from "@core/types";
import getServerSession from "@core/utils/getServerSession";
import {
  getPost,
  getPostQueryKey,
  getRelatedPosts,
  getRelatedPostsQueryKey,
  PostId,
} from "@features/community-v2";
import { PostThreadScreen } from "@features/community-v2/screens/PostThreadScreen";
import { GetServerSideProps } from "next";
import React from "react";
import { QueryClient } from "react-query";
import { dehydrate } from "react-query/hydration";

interface CommunityThreadPageProps {
  postId: PostId;
  ssrError: SSRError;
}

const CommunityThreadPage = ({
  postId,
  ssrError,
}: CommunityThreadPageProps): JSX.Element => {
  return <PostThreadScreen postId={postId} ssrError={ssrError} />;
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const queryClient = new QueryClient();
  const session = await getServerSession(ctx);

  queryClient.setQueryData(getSessionQueryKey(), session);

  // If an error happens during SSR, we want to capture it and pass it to the client.
  let ssrError: SSRError = null;

  // If the user is not logged in, we want to pre-fill the query cache,
  // so the content is in the initial HTML. This is needed for SEO.
  if (!session) {
    try {
      const post = await getPost(
        ctx.query.postId as string,
        session?.accessToken as string
      );

      queryClient.setQueryData(
        getPostQueryKey(
          ctx.query.postId as string,
          session?.accessToken as string
        ),
        post
      );

      const relatedPosts = await getRelatedPosts(ctx.query.postId as string);

      queryClient.setQueryData(
        getRelatedPostsQueryKey(ctx.query.postId as string) || [],
        relatedPosts
      );
    } catch (error) {
      if (
        error instanceof Error &&
        error?.name === APIErrorType.INVALID_PERMISSIONS
      ) {
        return {
          redirect: {
            destination: `/login?redirectTo=/community/posts/${
              ctx.query.postId as string
            }`,
            permanent: false,
          },
        };
      }

      if (
        error instanceof Error &&
        error?.name === APIErrorType.POST_DOESNT_EXIST
      ) {
        return {
          notFound: true,
        };
      }

      if (error instanceof Error) {
        ssrError = {
          name: error.name,
          message: error.message,
          stack: error.stack ?? null,
        };
      }
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      postId: ctx.query.postId as string,
      ssrError,
    },
  };
};

// The page is publicly accessible, so we want to show the OneTrust consent
// dialog to the user if they are not logged in.
CommunityThreadPage.shouldShowConsentDialog = true;
CommunityThreadPage.backgroundColor = "gray-v2";
CommunityThreadPage.hideNavbarShadowOnMobile = true;

export default CommunityThreadPage;
